import { useEffect, useRef, useState } from "react";
import { Loader2, Volume2, RotateCcw, Maximize } from "lucide-react";
import { ICE_SERVERS } from "@/lib/webrtc";

function textOn(hex) {
    const c = parseInt((hex || "#FF5500").replace("#", ""), 16);
    const lum = (0.299 * (c >> 16) + 0.587 * ((c >> 8) & 255) + 0.114 * (c & 255)) / 255;
    return lum > 0.6 ? "#08080C" : "#FFFFFF";
}

export function ScoreBug({ game }) {
    const homeColor = game.home_team?.primary_color || "#FF5500";
    const awayColor = game.away_team?.primary_color || "#A855F7";
    return (
        <div
            data-testid={`camera-scorebug-${game.id}`}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-stretch rounded-md overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.65)] border border-white/10"
        >
            <span className="flex items-center gap-1 bg-[#EF4444] px-2 py-1.5 text-[10px] font-display font-black uppercase tracking-widest text-white">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
            </span>
            <span
                className="px-2.5 py-1.5 font-display font-black uppercase text-sm tracking-wider"
                style={{ background: homeColor, color: textOn(homeColor) }}
            >
                {game.home_team?.short_name || "HOME"}
            </span>
            <span data-testid={`scorebug-home-score-${game.id}`} className="px-2 py-1.5 bg-white text-black font-stat font-bold text-sm">
                {game.home_score ?? 0}
            </span>
            <span className="flex items-center px-2 bg-black/85 font-stat text-[10px] font-bold text-[#FFB800] whitespace-nowrap">
                {(game.quarter || "").trim()}
                {game.clock ? ` ${game.clock.trim()}` : ""}
            </span>
            <span data-testid={`scorebug-away-score-${game.id}`} className="px-2 py-1.5 bg-white text-black font-stat font-bold text-sm">
                {game.away_score ?? 0}
            </span>
            <span
                className="px-2.5 py-1.5 font-display font-black uppercase text-sm tracking-wider"
                style={{ background: awayColor, color: textOn(awayColor) }}
            >
                {game.away_team?.short_name || "AWAY"}
            </span>
        </div>
    );
}

export default function LiveStreamPlayer({ game, onActive }) {
    const videoRef = useRef(null);
    const wrapRef = useRef(null);
    const wsRef = useRef(null);
    const pcRef = useRef(null);
    const pendingIce = useRef([]);
    const trackTimer = useRef(null);
    const [state, setState] = useState("idle");
    const [muted, setMuted] = useState(true);
    const [attempt, setAttempt] = useState(0);
    const [zoomed, setZoomed] = useState(false);

    useEffect(() => {
        const ws = new WebSocket(
            `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/stream/${game.id}?role=viewer`,
        );
        wsRef.current = ws;
        ws.onmessage = async (e) => {
            const msg = JSON.parse(e.data);
            if (msg.type === "viewer-id") {
                if (msg.broadcaster_live) setState("connecting");
            } else if (msg.type === "offer") {
                await handleOffer(msg.data);
            } else if (msg.type === "ice") {
                if (pcRef.current?.remoteDescription) pcRef.current.addIceCandidate(msg.data).catch(() => {});
                else pendingIce.current.push(msg.data);
            } else if (msg.type === "broadcaster-left") {
                teardownPeer();
                setState("idle");
                onActive(false);
            }
        };
        return () => {
            clearTimeout(trackTimer.current);
            ws.close();
            teardownPeer();
        };
    }, [game.id, attempt]);

    const teardownPeer = () => {
        pcRef.current?.close();
        pcRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
    };

    const handleOffer = async (offer) => {
        setState("connecting");
        teardownPeer();
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;
        pc.onicecandidate = (e) => {
            if (e.candidate && wsRef.current?.readyState === 1)
                wsRef.current.send(JSON.stringify({ type: "ice", data: e.candidate }));
        };
        pc.oniceconnectionstatechange = () => {
            if (pc.iceConnectionState === "failed") {
                clearTimeout(trackTimer.current);
                setState("failed");
                onActive(false);
            } else if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
                clearTimeout(trackTimer.current);
            }
        };
        pc.ontrack = (e) => {
            clearTimeout(trackTimer.current);
            if (videoRef.current) {
                videoRef.current.srcObject = e.streams[0];
                videoRef.current.play().catch(() => {});
            }
            setState("live");
            onActive(true);
        };
        clearTimeout(trackTimer.current);
        trackTimer.current = setTimeout(() => {
            if (pc.signalingState !== "closed" && !videoRef.current?.srcObject) {
                setState("failed");
                onActive(false);
            }
        }, 20000);
        await pc.setRemoteDescription(offer);
        for (const c of pendingIce.current) await pc.addIceCandidate(c).catch(() => {});
        pendingIce.current = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        wsRef.current?.send(JSON.stringify({ type: "answer", data: answer }));
    };

    const unmute = () => {
        if (videoRef.current) {
            videoRef.current.muted = false;
            videoRef.current.play().catch(() => {});
        }
        setMuted(false);
    };

    const toggleFullscreen = () => {
        const el = wrapRef.current;
        if (!el) return;
        const zoomFallback = () => {
            setZoomed(true);
            screen.orientation?.lock?.("landscape").catch(() => {});
        };
        if (zoomed) {
            setZoomed(false);
            screen.orientation?.unlock?.();
            return;
        }
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
            screen.orientation?.unlock?.();
        } else if (el.requestFullscreen) {
            el.requestFullscreen()
                .then(() => screen.orientation?.lock?.("landscape").catch(() => {}))
                .catch(zoomFallback);
        } else {
            zoomFallback();
        }
    };

    if (state === "idle") return null;

    return (
        <div
            ref={wrapRef}
            className={
                zoomed
                    ? "fixed inset-0 z-[100] bg-black"
                    : "aspect-video bg-black border-t border-[#262638] relative"
            }
            data-testid={`camera-stream-wrap-${game.id}`}
        >
            <video
                ref={videoRef}
                autoPlay
                playsInline
                controls
                muted={muted}
                data-testid={`camera-stream-${game.id}`}
                className="w-full h-full object-contain"
            />
            {state === "connecting" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70">
                    <Loader2 className="w-8 h-8 text-[#EF4444] animate-spin" />
                    <p className="text-xs uppercase tracking-[0.25em] text-slate-300 font-semibold">
                        Connecting to courtside camera…
                    </p>
                </div>
            )}
            {state === "failed" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/85 px-6">
                    <p className="text-xs uppercase tracking-[0.25em] text-slate-300 font-semibold text-center">
                        The video connection was blocked by the network
                    </p>
                    <button
                        data-testid={`camera-retry-${game.id}`}
                        onClick={() => {
                            setState("connecting");
                            setAttempt((a) => a + 1);
                        }}
                        className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                    >
                        <RotateCcw className="w-4 h-4" /> Retry Stream
                    </button>
                </div>
            )}
            {state === "live" && <ScoreBug game={game} />}
            {state !== "idle" && (
                <button
                    data-testid={`camera-fullscreen-${game.id}`}
                    onClick={toggleFullscreen}
                    className="absolute top-3 right-3 z-10 p-2 rounded-lg bg-[#08080C]/85 text-slate-300 hover:text-white border border-[#262638] transition-colors duration-200"
                    aria-label="Fullscreen"
                >
                    <Maximize className="w-4 h-4" />
                </button>
            )}
            {state === "live" && (
                <>
                    <span className="absolute top-3 left-3 flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#EF4444] text-white">
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Phone Cam
                    </span>
                    {muted && (
                        <button
                            data-testid={`camera-unmute-${game.id}`}
                            onClick={unmute}
                            className="absolute bottom-14 right-3 flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full shadow-lg transition-colors duration-200"
                        >
                            <Volume2 className="w-4 h-4" /> Tap for sound
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
